// Formatting helpers shared across views. Order timestamps arrive as
// `{ seconds }` objects (see orderService transformers).

const toDate = (value) => (value?.seconds !== undefined ? new Date(value.seconds * 1000) : new Date(value));

// Format date from timestamp object
export const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp.seconds * 1000).toLocaleDateString();
};

// Check if order is overdue (dispatch date is before today)
export const isOverdue = (timestamp, now = new Date()) => {
  if (!timestamp) return false;
  const dispatchDate = new Date(timestamp.seconds * 1000);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
  return dispatchDate < today;
};

// Format time duration between start and end timestamps as "Xh Ym Zs" or "Ym Zs"
export const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 'N/A';

  const durationMs = toDate(endTime) - toDate(startTime);

  const seconds = Math.floor((durationMs / 1000) % 60);
  const minutes = Math.floor((durationMs / (1000 * 60)) % 60);
  const hours = Math.floor(durationMs / (1000 * 60 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
};

// Escape values before interpolating them into print/label HTML so names containing
// &, <, >, or quotes can't corrupt the printed output.
export const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
  ));

// Unambiguous due date ("Thu 1 Oct") plus how far away it is, with an urgency level.
export const describeDueDate = (value, now = new Date()) => {
  if (!value) return null;
  const d = toDate(value);
  if (isNaN(d.getTime())) return null;
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((day - today) / 86400000);
  const label = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  let relative;
  if (days === 0) relative = 'Today';
  else if (days === 1) relative = 'Tomorrow';
  else if (days > 1) relative = `In ${days} days`;
  else if (days === -1) relative = '1 day overdue';
  else relative = `${-days} days overdue`;
  const urgency = days < 0 ? 'overdue' : days <= 2 ? 'soon' : 'normal';
  return { label, relative, urgency };
};
